/**
 * Rosalie's observation ledger: the durable, deterministic half of spec §5.3.
 *
 * The world declares what an observed id is drawn as, so saves retain ids and
 * wash cells rather than marks. A newer build which no longer places an old id
 * then draws nothing instead of a stale shape; more importantly, a build which
 * temporarily does not place it does not erase the player's note on its next
 * save. `restore()` therefore remembers ids without marks until `declare()`
 * supplies the current world's meaning for them.
 *
 * This module deliberately knows neither rooms nor rendering. It is fixed-step
 * data that must replay in Node exactly as it does in a browser.
 */

/** Spec §5.3's three forms, and there is no fourth. */
export type MarkKind = 'route' | 'timing' | 'door';

export interface MapPoint {
  readonly x: number;
  readonly z: number;
}

/** What one remembered thing is drawn as. Declared by the world, not the ledger. */
/**
 * What the thing she watched WAS, drawn as a picture on her sheet (D-239).
 * Absent is the old hand-drawn box: a thing, unnamed. The kind still decides
 * the shape of the mark (a route is a dashed line); the glyph names its subject.
 */
export type MarkGlyph = 'door' | 'person' | 'pig' | 'hook' | 'book';

export interface Mark {
  readonly kind: MarkKind;
  /** One point for `door`/`timing`; the polyline for `route`. Never empty. */
  readonly at: readonly MapPoint[];
  readonly glyph?: MarkGlyph;
}

export interface Observation {
  readonly id: string;
  /** The sheet it belongs on. A free string; the ledger does not know rooms. */
  readonly zone: string;
  readonly mark: Mark;
}

/** One metre is deliberately crude: this is where she walked, not a floor plan. */
export const WASH_CELL_METRES = 1;

export interface LedgerSnapshot {
  /** Observed ids. The MARK is not stored — the world re-declares it at boot. */
  readonly observed: readonly string[];
  /** zone -> packed cell keys, e.g. "3,-11". */
  readonly wash: Readonly<Record<string, readonly string[]>>;
}

function finitePoint(point: MapPoint): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.z);
}

function validMark(mark: Mark): boolean {
  return (mark.kind === 'route' || mark.kind === 'timing' || mark.kind === 'door')
    && mark.at.length > 0
    && mark.at.every(finitePoint);
}

function validObservation(observation: Observation): boolean {
  return observation.id.length > 0 && observation.zone.length > 0 && validMark(observation.mark);
}

function copyObservation(observation: Observation): Observation {
  return {
    id: observation.id,
    zone: observation.zone,
    mark: {
      kind: observation.mark.kind,
      at: observation.mark.at.map((point) => ({ x: point.x, z: point.z })),
      ...(observation.mark.glyph === undefined ? {} : { glyph: observation.mark.glyph }),
    },
  };
}

function cellKey(x: number, z: number): string {
  return `${x},${z}`;
}

function readCellKey(key: string): MapPoint | null {
  const comma = key.indexOf(',');
  if (comma <= 0 || comma !== key.lastIndexOf(',')) return null;
  const x = Number(key.slice(0, comma));
  const z = Number(key.slice(comma + 1));
  if (!Number.isFinite(x) || !Number.isFinite(z) || !Number.isInteger(x) || !Number.isInteger(z)) return null;
  // Saves only accept the spelling this module writes. Without this, a malformed
  // value such as "3, 4" can survive a read and later produce a second spelling
  // for the same cell, making a washed area depend on which build wrote it.
  return cellKey(x, z) === key ? { x, z } : null;
}

function comparePoints(a: MapPoint, b: MapPoint): number {
  return a.z - b.z || a.x - b.x;
}

function snapshotRecord(wash: ReadonlyMap<string, ReadonlySet<string>>): Readonly<Record<string, readonly string[]>> {
  const record: Record<string, readonly string[]> = {};
  for (const zone of [...wash.keys()].sort()) {
    const cells = wash.get(zone);
    if (cells === undefined) continue;
    record[zone] = [...cells]
      .map((key) => ({ key, point: readCellKey(key) }))
      .filter((entry): entry is { key: string; point: MapPoint } => entry.point !== null)
      .sort((a, b) => comparePoints(a.point, b.point))
      .map((entry) => entry.key);
  }
  return record;
}

/** A deterministic record of what she observed and where she has walked. */
export class Ledger {
  /** `null` is an id restored before the current world has re-declared its mark. */
  readonly #observed = new Map<string, Observation | null>();
  readonly #wash = new Map<string, Set<string>>();

  /** Record the first declaration of an observed id; later records must not redraw history. */
  record(observation: Observation): void {
    if (!validObservation(observation) || this.#observed.has(observation.id)) return;
    this.#observed.set(observation.id, copyObservation(observation));
  }

  has(id: string): boolean {
    return this.#observed.has(id);
  }

  /** Attach this world's current mark to an id retained by a save, if it was observed. */
  declare(observation: Observation): void {
    if (!validObservation(observation) || this.#observed.get(observation.id) !== null) return;
    this.#observed.set(observation.id, copyObservation(observation));
  }

  observations(zone: string): readonly Observation[] {
    const entries: Observation[] = [];
    for (const observation of this.#observed.values()) {
      if (observation !== null && observation.zone === zone) entries.push(copyObservation(observation));
    }
    return entries;
  }

  visit(zone: string, x: number, z: number): void {
    if (zone.length === 0 || !Number.isFinite(x) || !Number.isFinite(z)) return;
    const cellX = Math.floor(x / WASH_CELL_METRES);
    const cellZ = Math.floor(z / WASH_CELL_METRES);
    const cells = this.#wash.get(zone) ?? new Set<string>();
    cells.add(cellKey(cellX, cellZ));
    this.#wash.set(zone, cells);
  }

  wash(zone: string): readonly MapPoint[] {
    const cells = this.#wash.get(zone);
    if (cells === undefined) return [];
    return [...cells]
      .map(readCellKey)
      .filter((point): point is MapPoint => point !== null)
      .sort(comparePoints)
      .map((point) => ({ x: point.x, z: point.z }));
  }

  zones(): readonly string[] {
    const zones = new Set(this.#wash.keys());
    for (const observation of this.#observed.values()) {
      if (observation !== null) zones.add(observation.zone);
    }
    return [...zones].sort();
  }

  snapshot(): LedgerSnapshot {
    return {
      observed: [...this.#observed.keys()],
      wash: snapshotRecord(this.#wash),
    };
  }

  static restore(snapshot: unknown): Ledger {
    const ledger = new Ledger();
    if (typeof snapshot !== 'object' || snapshot === null || Array.isArray(snapshot)) return ledger;
    const candidate = snapshot as Readonly<Record<string, unknown>>;
    const observed = candidate['observed'];
    if (Array.isArray(observed)) {
      for (const id of observed) {
        if (typeof id === 'string' && id.length > 0 && !ledger.#observed.has(id)) ledger.#observed.set(id, null);
      }
    }
    const wash = candidate['wash'];
    if (typeof wash !== 'object' || wash === null || Array.isArray(wash)) return ledger;
    for (const [zone, rawCells] of Object.entries(wash)) {
      if (zone.length === 0 || !Array.isArray(rawCells)) continue;
      for (const key of rawCells) {
        if (typeof key !== 'string' || readCellKey(key) === null) continue;
        const cells = ledger.#wash.get(zone) ?? new Set<string>();
        cells.add(key);
        ledger.#wash.set(zone, cells);
      }
    }
    return ledger;
  }
}
